import { baseApi as api } from 'shared/api/baseApi';
import { getCookie } from 'shared/utils';
const injectedRtkApi = api.injectEndpoints({
	endpoints: (build) => ({
		channelsList: build.query<ChannelsListApiResponse, ChannelsListApiArg>({
			query: (queryArg) => ({
				url: `/api/channels/`,
				params: {
					limit: queryArg.limit,
					name: queryArg.name,
					offset: queryArg.offset,
					ordering: queryArg.ordering,
					status: queryArg.status,
				},
			}),
		}),
		channelsRetrieve: build.query<ChannelsRetrieveApiResponse, ChannelsRetrieveApiArg>({
			query: (queryArg) => ({ url: `/api/channels/${queryArg}/` }),
		}),
		channelsUpdate: build.mutation<ChannelsUpdateApiResponse, ChannelsUpdateApiArg>({
			query: (queryArg) => ({
				url: `/api/channels/${queryArg.uuid}/`,
				method: 'PUT',
				body: queryArg.channel,
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
		channelsPartialUpdate: build.mutation<ChannelsPartialUpdateApiResponse, ChannelsPartialUpdateApiArg>({
			query: (queryArg) => ({ url: `/api/channels/${queryArg.uuid}/`, method: 'PATCH', body: queryArg.patchedChannel }),
		}),
		channelsDestroy: build.mutation<ChannelsDestroyApiResponse, ChannelsDestroyApiArg>({
			query: (queryArg) => ({
				url: `/api/channels/${queryArg}/`,
				method: 'DELETE',
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
	}),
	overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type ChannelsListApiResponse = /** status 200  */ PaginatedChannelListRead;
export type ChannelsListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	name?: string;
	/** The initial index from which to return the results. */
	offset?: number;
	/** Which field to use when ordering the results. */
	ordering?: string;
	/** * `active` - ACTIVE
	 * `restricted` - RESTRICTED
	 * `not_admin` - NOT_ADMIN
	 * `banned` - BANNED
	 * `not_enough_subscribers` - NOT_ENOUGH_SUBSCRIBERS
	 * `rejected` - REJECTED */
	status?: ('active' | 'banned' | 'not_admin' | 'not_enough_subscribers' | 'rejected' | 'restricted')[];
};
export type ChannelsRetrieveApiResponse = /** status 200  */ ChannelRead;
export type ChannelsRetrieveApiArg = /** A UUID string identifying this channel. */ string;
export type ChannelsUpdateApiResponse = /** status 200  */ ChannelRead;
export type ChannelsUpdateApiArg = {
	/** A UUID string identifying this channel. */
	uuid: string;
	channel: Channel;
};
export type ChannelsPartialUpdateApiResponse = /** status 200  */ ChannelRead;
export type ChannelsPartialUpdateApiArg = {
	/** A UUID string identifying this channel. */
	uuid: string;
	patchedChannel: PatchedChannel;
};
export type ChannelsDestroyApiResponse = unknown;
export type ChannelsDestroyApiArg = /** A UUID string identifying this channel. */ string;
export type Channel = {
	channel_type?: ChannelTypeEnum;
	categories?: string[];
	languages?: string[];
	countries?: string[];
};
export type ChannelBotMembership = {};
export type ChannelBotMembershipRead = {
	bot_status: BotStatusEnum;
	can_be_edited: boolean;
	can_manage_chat: boolean;
	can_delete_messages: boolean;
	can_manage_video_chats: boolean;
	can_restrict_members: boolean;
	can_promote_members: boolean;
	can_change_info: boolean;
	can_invite_users: boolean;
	can_post_stories: boolean;
	can_edit_stories: boolean;
	can_delete_stories: boolean;
	can_post_messages: boolean;
	can_edit_messages: boolean;
	can_pin_messages: boolean;
	can_manage_topics: boolean;
};
export type PeriodStats = {
	approved_actions_count: number;
	clicks_count: number;
	earned: string;
	total_actions_count: number;
};
export type PeriodStatsRead = {
	avg_income_per_post: string | null;
	avg_income_per_action: string | null;
	approved_actions_count: number;
	clicks_count: number;
	earned: string;
	total_actions_count: number;
};
export type Metrics = {
	current_period: PeriodStats;
	prev_period: PeriodStats;
};
export type MetricsRead = {
	current_period: PeriodStatsRead;
	prev_period: PeriodStatsRead;
};
export type ChannelRead = {
	bot_membership: ChannelBotMembershipRead;
	uuid: string;
	name: string;
	logo_url: string | null;
	description: string;
	subscribers_count: number;
	type: TypeEnum;
	status: ChannelStatusEnum;
	rejection_comment: string;
	channel_type?: ChannelTypeEnum;
	categories?: string[];
	languages?: string[];
	countries?: string[];
	metrics: MetricsRead;
};
export type PaginatedChannelList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: Channel[];
};
export type PaginatedChannelListRead = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: ChannelRead[];
};
export type PatchedChannel = {
	channel_type?: ChannelTypeEnum;
	categories?: string[];
	languages?: string[];
	countries?: string[];
};
export type PatchedChannelRead = {
	bot_membership?: ChannelBotMembershipRead;
	uuid?: string;
	name?: string;
	logo_url?: string | null;
	description?: string;
	subscribers_count?: number;
	type?: TypeEnum;
	status?: ChannelStatusEnum;
	rejection_comment?: string;
	channel_type?: ChannelTypeEnum;
	categories?: string[];
	languages?: string[];
	countries?: string[];
	metrics?: MetricsRead;
};
export enum ChannelTypeEnum {
	Content = 'content',
	Promo = 'promo',
}
export enum BotStatusEnum {
	Administrator = 'administrator',
	Restricted = 'restricted',
	Member = 'member',
	Left = 'left',
	Kicked = 'kicked',
}
export enum TypeEnum {
	TelegramChannel = 'telegram_channel',
	TelegramGroup = 'telegram_group',
}
export enum ChannelStatusEnum {
	Active = 'active',
	Restricted = 'restricted',
	NotAdmin = 'not_admin',
	Banned = 'banned',
	NotEnoughSubscribers = 'not_enough_subscribers',
	Rejected = 'rejected',
}
